'use client';

import { useRef } from 'react';
import { useInViewOnce } from './useInViewOnce';

/**
 * Rises and fades in the first time it scrolls into view, then stays put.
 *
 * Nothing is hidden until this component's own JavaScript has run: the hidden
 * state applies only to elements carrying `data-reveal-armed`, which is set
 * here, not in the server HTML and not by a media query. If the bundle never
 * executes (a stale chunk, a CSP block, a hydration error) the content simply
 * stays visible, which is the correct failure.
 *
 * Elements already in view when the effect runs are revealed immediately and
 * never armed, so nothing on screen blinks out and back in after hydration.
 */
export function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useInViewOnce(ref, () => ref.current?.setAttribute('data-revealed', 'true'), {
    rootMargin: '0px 0px -10% 0px',
    immediate: true,
    onArm: () => ref.current?.setAttribute('data-reveal-armed', 'true'),
  });

  return (
    <div
      ref={ref}
      className={`reveal ${className}`}
      style={delay ? ({ '--reveal-delay': `${delay}ms` } as React.CSSProperties) : undefined}
    >
      {children}
    </div>
  );
}
